/**
 * Axios HTTP client — JWT auto-attach + envelope handling + silent refresh.
 *
 * refresh-token-family-design 모델:
 *   - access: 메모리(zustand). 매 요청 `Authorization: Bearer`.
 *   - refresh: **웹 = HttpOnly 쿠키**(`withCredentials`) / **네이티브 = SecureStorage → `X-Refresh-Token` 헤더**.
 *   - 콜드스타트: 앱 init 에서 {@link bootstrapAuth} 1회 → access 복구.
 *
 *   response flow:
 *     HTTP 2xx + envelope.code S2xx → pass through (data 만 unwrap)
 *     HTTP 2xx + envelope.code E4xx 또는 B1xxx → reject ApiError
 *     HTTP 401 → single-flight refresh → 원요청 재시도. 실패 시 clear.
 *     HTTP 4xx 또는 5xx → reject ApiError (envelope 우선, 없으면 generic)
 *
 * ⚠️ Capacitor 분기(§18(c)): CapacitorHttp.enabled=true 라 네이티브에서 쿠키 자동전송이 보장되지
 *   않는다. 따라서 web=쿠키(`withCredentials`), native=`X-Refresh-Token` 헤더로 분기한다.
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "../store/authStore";
import { getEffectiveApiBase } from "../config/env";
import { isNative } from "../native";
import { ApiError, isApiEnvelope, type ApiEnvelope } from "../lib/errors";
import i18n from "../i18n";

export function userBaseUrl(): string {
  let base = getEffectiveApiBase().replace(/\/$/, "");
  // CapacitorHttp(native) 버그 회피: 상대경로 POST + 헤더 시 setRequestHeader 가 열리지 않은
  // 실제 XHR 로 위임돼 "state must be OPENED" 가 난다. 네이티브에서 상대 base 면 WebView origin
  // 기준 절대 URL 로 만들어 native HTTP shim 이 처리하게 한다(같은 프록시 경로 유지).
  // 웹/절대 base(dev·prod)에는 영향 없음.
  if (base.startsWith("/") && isNative() && typeof window !== "undefined") {
    base = window.location.origin + base;
  }
  return `${base}/user`;
}

/**
 * `/user` 프리픽스가 없는 API 루트(…/api/v1) — assets 등 user 도메인 밖 공용 endpoint 용.
 * client 의 baseURL 은 요청 인터셉터가 항상 userBaseUrl() 로 덮으므로, 이 base 로는
 * **절대 URL 을 만들어** 호출해야 한다(axios 는 절대 URL 이면 baseURL 을 무시).
 */
export function apiRootUrl(): string {
  let base = getEffectiveApiBase().replace(/\/$/, "");
  if (base.startsWith("/") && typeof window !== "undefined") {
    base = window.location.origin + base;
  }
  return base;
}

/** 사용자 도메인 API base (모든 user endpoint 는 /api/v1/user/** 하위) */
export const client: AxiosInstance = axios.create({
  baseURL: userBaseUrl(),
  timeout: 10000,
  // 웹: refresh HttpOnly 쿠키 자동 전송. 네이티브: 무해(헤더로 별도 전송).
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  paramsSerializer: { indexes: null },
});

// === Request interceptor — JWT 자동 첨부 + 동적 baseURL ===
client.interceptors.request.use((config) => {
  config.baseURL = userBaseUrl();
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  if (isNative()) {
    // 네이티브 클라이언트 표식 — BE 가 이 헤더가 있을 때만 응답 바디에 refreshToken 을 실어준다.
    config.headers.set("X-Client-Native", "1");
    // 네이티브는 쿠키를 안 쓴다(헤더+SecureStorage). withCredentials 는 CapacitorCookies 를
    // 끌어들여 응답 처리에 간섭할 수 있어 끈다. (웹만 쿠키 자동전송 필요)
    config.withCredentials = false;
  } else {
    config.withCredentials = true;
  }
  return config;
});

// === Silent refresh (single-flight) ===
let refreshPromise: Promise<string> | null = null;

/** refresh 토큰 / 로그인 / 로그아웃 등 재시도해선 안 되는 인증 엔드포인트인지. */
function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes("/auth/refresh") ||
    url.includes("/login") ||
    url.includes("/auth/logout")
  );
}

/**
 * refresh 1회 실행 — 웹=쿠키 / 네이티브=SecureStorage 헤더. 새 access 를 store 에 반영하고 반환.
 * 네이티브에 refresh 가 없으면 "No refresh token" throw → 호출 측이 비로그인으로 처리.
 */
async function attemptRefresh(): Promise<string> {
  const { auth } = await import("./auth");
  let res;
  if (isNative()) {
    const { readNativeRefresh } = await import("./refreshSecureStore");
    const token = await readNativeRefresh();
    if (!token) {
      throw new Error("No refresh token");
    }
    res = await auth.refresh(token);
  } else {
    res = await auth.refresh(); // 웹: 쿠키 자동전송, 바디 없음
  }
  useAuthStore.getState().setTokens(res.accessToken, res.refreshToken);
  return res.accessToken;
}

/**
 * 콜드스타트 부트스트랩 — 앱 init 에서 1회 호출. access 메모리 소멸 후 refresh 로 복구.
 *
 * single-flight `refreshPromise` 를 **시드**(§18d)해, mount 직후 큐 폴 등에서 발생하는 401 이
 * 별도 회전을 일으키지 않고 같은 promise 를 await 하게 한다(이중 회전 방지).
 *
 * @returns true=세션 복구, false=비로그인(refresh 없음/만료)
 * @throws 일시 장애(5xx/네트워크) — 호출 측이 재시도 UI 처리. 토큰은 clear 하지 않음.
 */
export async function bootstrapAuth(): Promise<boolean> {
  const p = (refreshPromise ??= attemptRefresh());
  try {
    await p;
    return true;
  } catch (err: unknown) {
    const httpStatus = err instanceof ApiError ? err.httpStatus : undefined;
    const noRefresh = err instanceof Error && err.message === "No refresh token";
    if (httpStatus === 401 || noRefresh) {
      // refresh 만료/없음 = 로그인 필요. 조용히 비로그인 유지.
      useAuthStore.getState().clear();
      return false;
    }
    // 일시 장애(5xx/네트워크) — 재시도 UI. 토큰 보존.
    throw err;
  } finally {
    // 우리가 시드한 promise 만 정리 — 사이에 새로 시드된 single-flight 를 지우지 않음.
    if (refreshPromise === p) refreshPromise = null;
  }
}

/**
 * in-flight refresh 가 끝날 때까지 대기 (성공/실패 무관).
 * logout() 이 호출해, refresh 가 clear 이후 resolve 하며 토큰을 되살리는 부활 레이스를 방지.
 */
export function awaitPendingRefresh(): Promise<void> {
  return refreshPromise ? refreshPromise.then(() => {}, () => {}) : Promise.resolve();
}

// === Response interceptor — envelope 분석 + 401 silent refresh ===
client.interceptors.response.use(
  (res: AxiosResponse) => {
    const body = res.data as unknown;
    if (isApiEnvelope(body)) {
      if (body.code.startsWith("S")) return res;
      throw new ApiError(body.code, body.message, {
        httpStatus: res.status,
        data: body.data,
      });
    }
    return res;
  },
  async (error: AxiosError<ApiEnvelope>) => {
    const status = error.response?.status;
    const env = error.response?.data;
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (status === 401) {
      const retryable =
        !!originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest.url);

      if (retryable) {
        originalRequest._retry = true;
        const p = (refreshPromise ??= attemptRefresh());
        try {
          await p;
          // 재시도 — request 인터셉터가 store 의 새 accessToken 을 다시 첨부한다.
          return client(originalRequest);
        } catch {
          // refresh 실패(만료/폐기/네트워크/네이티브 토큰없음) → 로그아웃 후 아래에서 원에러 변환.
          useAuthStore.getState().clear();
        } finally {
          if (refreshPromise === p) refreshPromise = null;
        }
      } else {
        // 재시도 불가(auth endpoint / 이미 재시도) → 로그아웃.
        useAuthStore.getState().clear();
      }
    }

    if (env && isApiEnvelope(env)) {
      throw new ApiError(env.code, env.message, { httpStatus: status, data: env.data });
    }
    throw new ApiError(
      `E${status ?? 500}`,
      error.message ?? i18n.t("error.networkFailure"),
      { httpStatus: status },
    );
  },
);

/** envelope 의 data 만 unwrap 하는 helper. 일반 GET / POST 에 권장. */
export async function apiGet<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const res = await client.get<ApiEnvelope<T>>(path, { params });
  return (res.data.data ?? null) as T;
}

export async function apiPost<T>(path: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await client.post<ApiEnvelope<T>>(path, body, config);
  return (res.data.data ?? null) as T;
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await client.put<ApiEnvelope<T>>(path, body);
  return (res.data.data ?? null) as T;
}

export async function apiDelete<T = void>(path: string): Promise<T> {
  const res = await client.delete<ApiEnvelope<T>>(path);
  return (res.data.data ?? null) as T;
}
